/** Smallest side a profile photo may have (the server refuses smaller ones). */
const MIN_SIDE = 64;

async function decode(
  file: Blob,
): Promise<{ source: CanvasImageSource; width: number; height: number; done: () => void }> {
  if (typeof createImageBitmap === 'function') {
    // 'from-image' applies the camera's EXIF rotation, so portrait photos stay upright.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    return { source: bitmap, width: bitmap.width, height: bitmap.height, done: () => bitmap.close() };
  }
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.src = url;
  await img.decode();
  return {
    source: img,
    width: img.naturalWidth,
    height: img.naturalHeight,
    done: () => URL.revokeObjectURL(url),
  };
}

/**
 * A profile photo from any picture the player picks: centre-cropped to a square, at most `size` pixels, re-encoded
 * as JPEG. Nothing of the original file (camera data, location) survives the canvas.
 */
export async function squareJpeg(file: Blob, size = 512, quality = 0.86): Promise<Blob> {
  const image = await decode(file);
  try {
    const side = Math.min(image.width, image.height);
    if (side < MIN_SIDE) throw new Error('too small');
    const out = Math.min(size, side);
    const canvas = document.createElement('canvas');
    canvas.width = out;
    canvas.height = out;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no canvas');
    ctx.drawImage(
      image.source,
      (image.width - side) / 2,
      (image.height - side) / 2,
      side,
      side,
      0,
      0,
      out,
      out,
    );
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))),
        'image/jpeg',
        quality,
      ),
    );
  } finally {
    image.done();
  }
}
